/**
 * 共有キャンバス（RT-05 / Phase 3-A）— 部屋一覧とキャンバス本体
 *
 * anokoe-design: ①事実性 > ②a11y > ③タスク > ④演出 > ⑤美観。
 * トキワ荘の役割色は Violet（面=#8b5cf6 / 文字=#6d28d9）。役割色は識別専用。
 *
 * 【事実性（決定31）】在室者数は3値で扱う:
 *   0 → 「いまは誰もいません」（先に入って待てる）／1以上 → 実数／**null → 「確認中」**（人数を出さない）
 */
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { api, CanvasRoom, CanvasStroke } from '../services/api';
import { PulseButton } from '../components/ui/PulseButton';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { TownShell, TownLoading, TownError, TownEmpty, useTownUser, timeAgo } from '../components/town/townShared';
import { useCanvasRoom, ConnState } from '../hooks/useCanvasRoom';
import { BrushControls, BRUSH_COLORS } from '../components/town/BrushControls';

type Load = 'loading' | 'ready' | 'error';

// ══════════════════════════════════════════════════════════
// 部屋一覧 /tokiwa/rooms
// ══════════════════════════════════════════════════════════
export const TokiwaRooms: FC = () => {
  const [, setLocation] = useLocation();
  const { requireLogin, loginGate } = useTownUser();
  const { playSound } = useGamenics();
  const [rooms, setRooms] = useState<CanvasRoom[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [state, setState] = useState<Load>('loading');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setState('loading');
    api.rtListRooms()
      .then(({ rooms, disabled }) => { setRooms(rooms); setDisabled(disabled); setState('ready'); })
      .catch(() => setState('error'));
  }, []);
  useEffect(load, [load]);

  return (
    <TownShell role="illustration" kicker="SHARED CANVAS" title="合作部屋">
      <p className="text-ink-2 text-sm mb-6 max-w-2xl">
        同じキャンバスに、同時に描く部屋です。1部屋は4人まで。描いた線はその場で全員に届きます。
      </p>

      {/* フラグOFF: 準備中を正直に出し、部屋を開く導線は出さない（事実性①） */}
      {state === 'ready' && disabled ? (
        <div className="pop-card p-6 text-center">
          <p className="text-ink-2">共有キャンバスはいま準備中です。</p>
          <p className="text-[12px] text-ink-3 mt-1">開いたらトキワ荘からご案内します。</p>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => setCreating(true)); }}>
              部屋を開く
            </PulseButton>
          </div>

          {state === 'loading' && <TownLoading />}
          {state === 'error' && <TownError message="部屋を読み込めませんでした" onRetry={load} />}
          {state === 'ready' && rooms.length === 0 && (
            <TownEmpty title="いまは開いている部屋がありません。最初の一部屋をあなたから。">
              <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => setCreating(true)); }}>
                部屋を開く
              </PulseButton>
            </TownEmpty>
          )}
          {state === 'ready' && rooms.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 list-none p-0 m-0">
              {rooms.map(r => (
                <li key={r.id}>
                  <button
                    onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(`/tokiwa/rooms/${r.id}`); }}
                    className="pop-card w-full text-left p-5 min-h-[44px] hover:-translate-y-0.5 transition-transform"
                  >
                    <span className="font-display font-bold text-ink block">{r.title}</span>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-ink-3">
                      <Occupancy count={r.participant_count} />
                      <span className="tabular-nums">線 {r.stroke_count}</span>
                      <span className="tabular-nums ml-auto">{timeAgo(r.created_at)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {creating && (
        <RoomComposer
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); setLocation(`/tokiwa/rooms/${id}`); }}
        />
      )}
      {loginGate}
    </TownShell>
  );
};

/**
 * 在室者数の表示（決定31・事実性①）。
 * null は「不明」であって 0 ではないため、人数を出さず「確認中」と言う。
 */
const Occupancy: FC<{ count: number | null }> = ({ count }) => {
  if (count === null || count === undefined) {
    return <span className="text-ink-3">在室：確認中</span>;
  }
  if (count === 0) {
    return <span className="text-ink-2">いまは誰もいません（先に入って待てます）</span>;
  }
  return (
    <span className="text-role-illustration-text font-bold">
      在室 <span className="tabular-nums">{count}</span> 人
    </span>
  );
};

const RoomComposer: FC<{ onClose: () => void; onCreated: (id: string) => void }> = ({ onClose, onCreated }) => {
  const { playSound } = useGamenics();
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!title.trim()) { setError('部屋の名前を入れてください'); return; }
    setBusy(true); setError(null);
    const r = await api.rtCreateRoom(title.trim());
    setBusy(false);
    if (!r.success || !r.id) {
      setError(r.code === 'FEATURE_DISABLED' ? '共有キャンバスはいま準備中です' : (r.error || '部屋を開けませんでした'));
      return;
    }
    playSound(GamenicsEvent.SUBMISSION_COMPLETE);
    onCreated(r.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="部屋を開く">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border-2 border-ink rounded-pop p-6 w-full max-w-md">
        <h2 className="text-xl font-display font-bold mb-4">部屋を開く</h2>
        <label className="block text-sm text-ink-2 mb-1" htmlFor="room-title">部屋の名前</label>
        <input id="room-title" value={title} onChange={e => setTitle(e.target.value)} maxLength={80}
          className="pop-field w-full mb-2" placeholder="なにを描く部屋？" autoFocus />
        <p className="text-[11px] text-ink-3 mb-3">4人まで一緒に描けます。閉じるまで誰でも入れます。</p>
        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        <div className="flex gap-3">
          <button className="btn btn-ghost flex-1 min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}>やっぱりやめる</button>
          <PulseButton isPrimary className="flex-1" onClick={save} disabled={busy}>
            {busy ? '開いています…' : 'この部屋を開く'}
          </PulseButton>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// 部屋（キャンバス本体） /tokiwa/rooms/:id
// ══════════════════════════════════════════════════════════
export const TokiwaRoom = ({ params }: any) => {
  const roomId: string = params?.id;
  const [, setLocation] = useLocation();
  const { user, ready, promptLogin, loginGate } = useTownUser();
  const { playSound } = useGamenics();
  const [room, setRoom] = useState<CanvasRoom | null>(null);
  const [roomState, setRoomState] = useState<Load>('loading');

  useEffect(() => {
    api.rtGetRoom(roomId)
      .then(({ room }) => { setRoom(room); setRoomState(room ? 'ready' : 'error'); })
      .catch(() => setRoomState('error'));
  }, [roomId]);

  // 未ログインでは入室できない（描いた線に作者が必要なため）
  const canConnect = ready && !!user && roomState === 'ready' && room?.status === 'open';

  const {
    state, error, strokes, historyLoading, participants, me, cursors, closedReason,
    sendStroke, sendCursor, undoMine, reconnect, canUndo,
  } = useCanvasRoom(roomId, canConnect);

  return (
    <TownShell
      role="illustration"
      kicker="SHARED CANVAS"
      title={room?.title || '合作部屋'}
      actions={
        <button className="btn btn-ghost btn-sm min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/tokiwa/rooms'); }}>
          部屋の一覧へ
        </button>
      }
    >
      {roomState === 'loading' && <TownLoading />}
      {roomState === 'error' && <TownError message="この部屋を開けませんでした" onRetry={() => setLocation('/tokiwa/rooms')} />}

      {roomState === 'ready' && room && (
        <>
          {/* 状態表示は aria-live で読み上げる（a11y） */}
          <div className="flex items-center gap-3 flex-wrap mb-4" aria-live="polite">
            <ConnBadge state={state} />
            <ParticipantList participants={participants} meId={me?.userId} />
          </div>

          {/* 閉室・未ログイン・エラーはキャンバスの手前で伝える */}
          {room.status !== 'open' || closedReason ? (
            <div className="pop-card p-6 text-center">
              <p className="text-ink font-bold mb-2">この部屋は閉じました</p>
              <p className="text-[12px] text-ink-3 mb-4">描かれた線は残っています。</p>
              <button className="btn btn-primary min-h-[44px]" onClick={() => setLocation('/tokiwa/rooms')}>部屋の一覧へ</button>
            </div>
          ) : !ready ? (
            <TownLoading label="確認しています" />
          ) : !user ? (
            <div className="pop-card p-6 text-center">
              <p className="text-ink-2 mb-4">部屋に入るにはログインが必要です（描いた線に作者が残ります）。</p>
              <button className="btn btn-primary min-h-[44px]" onClick={promptLogin}>ログインする</button>
            </div>
          ) : (
            <>
              {error && <RoomErrorNotice error={error} onBack={() => setLocation('/tokiwa/rooms')} />}
              {state === 'disconnected' && !closedReason && (
                <div className="pop-card p-4 mb-4 flex items-center gap-3 flex-wrap">
                  <span className="text-ink-2 text-sm flex-1">つながりが切れました。</span>
                  <button className="btn btn-primary min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); reconnect(); }}>
                    もう一度つなぐ
                  </button>
                </div>
              )}

              <CanvasBoard
                strokes={strokes}
                historyLoading={historyLoading}
                cursors={cursors}
                participants={participants}
                meId={me?.userId}
                connected={state === 'connected'}
                onStroke={sendStroke}
                onCursor={sendCursor}
                onUndo={() => { if (!undoMine()) { /* 自分の線が無いときは何もしない */ } }}
                canUndo={canUndo}
              />

              {/* スクリーンリーダー利用者向けの代替導線（キャンバスは代替表現を作れないため正直に伝える） */}
              <p className="text-[12px] text-ink-3 mt-3">
                ここは絵を描く場所です。画面を読み上げてお使いの方は、
                <button className="underline text-role-illustration-text min-h-[44px]" onClick={() => setLocation('/tokiwa/board')}>トキワ荘の掲示板</button>
                で会話に参加できます。いま部屋にいるのは {participants.map(p => p.displayName).join('、') || 'あなただけ'} です。
              </p>

              {/* hostだけが閉じられる */}
              {user && room.host_id === user.id && (
                <div className="mt-4">
                  <button
                    className="btn btn-ghost min-h-[44px]"
                    onClick={async () => {
                      if (!confirm('この部屋を閉じますか？（描いた線は残ります）')) return;
                      const r = await api.rtCloseRoom(roomId);
                      if (r.success) setLocation('/tokiwa/rooms');
                    }}
                  >
                    部屋を閉じる
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
      {loginGate}
    </TownShell>
  );
};

/** 接続状態バッジ（5状態）。パルスは reduced-motion で静的化 */
const ConnBadge: FC<{ state: ConnState }> = ({ state }) => {
  const map: Record<ConnState, { label: string; cls: string; pulse: boolean }> = {
    connecting: { label: 'つないでいます', cls: 'bg-cream-2 text-ink-2 border-ink/20', pulse: true },
    connected: { label: 'つながっています', cls: 'bg-role-illustration-soft text-role-illustration-text border-role-illustration', pulse: false },
    reconnecting: { label: 'つなぎ直しています', cls: 'bg-sun text-ink border-ink', pulse: true },
    disconnected: { label: '切れています', cls: 'bg-cream-2 text-ink-2 border-ink/20', pulse: false },
    error: { label: 'うまくつながりません', cls: 'bg-cream-2 text-pop-dark border-pop-dark', pulse: false },
  };
  const m = map[state];
  return (
    <span className={`inline-flex items-center gap-2 text-[12px] font-bold px-3 py-1.5 rounded-full border-2 ${m.cls}`} role="status">
      <span className={`w-2 h-2 rounded-full bg-current ${m.pulse ? 'motion-safe:animate-pulse' : ''}`} aria-hidden />
      {m.label}
    </span>
  );
};

const ParticipantList: FC<{ participants: { userId: string; displayName: string }[]; meId?: string }> = ({ participants, meId }) => (
  <span className="text-[12px] text-ink-2">
    {participants.length === 0 ? (
      'いまは誰もいません'
    ) : (
      <>
        いま <span className="tabular-nums font-bold">{participants.length}</span> 人：
        {participants.map(p => (
          <span key={p.userId} className="ml-1">
            {p.displayName}{p.userId === meId ? '（あなた）' : ''}
          </span>
        ))}
      </>
    )}
  </span>
);

const RoomErrorNotice: FC<{ error: { code: string; message: string }; onBack: () => void }> = ({ error, onBack }) => {
  // 文言変換表準拠。コードごとに事実を伝える
  const text: Record<string, string> = {
    room_full: 'この部屋はいっぱいです（4人まで）',
    too_many_connections: '同じアカウントで開きすぎています。ほかのタブを閉じてください',
    room_full_strokes: 'この部屋は描き切りました',
    not_your_stroke: '自分の線だけ戻せます',
    FEATURE_DISABLED: '共有キャンバスはいま準備中です',
  };
  const msg = text[error.code] || error.message || 'うまくいきませんでした';
  const showBack = error.code === 'room_full' || error.code === 'FEATURE_DISABLED';
  return (
    <div className="pop-card p-4 mb-4 flex items-center gap-3 flex-wrap" role="alert">
      <span className="text-ink-2 text-sm flex-1">{msg}</span>
      {showBack && <button className="btn btn-ghost min-h-[44px]" onClick={onBack}>部屋の一覧へ</button>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// キャンバス本体
// ══════════════════════════════════════════════════════════
/** 参加者ごとのカーソル色（役割色は識別専用なので、人の識別にはこの並びを使う） */
const CURSOR_COLORS = ['#8b5cf6', '#ec4899', '#06b6a4', '#f59e0b'];

const CanvasBoard: FC<{
  strokes: CanvasStroke[];
  historyLoading: boolean;
  cursors: Record<string, { userId: string; x: number; y: number }>;
  participants: { userId: string; displayName: string }[];
  meId?: string;
  connected: boolean;
  onStroke: (s: CanvasStroke) => void;
  onCursor: (x: number, y: number) => void;
  onUndo: () => void;
  canUndo: () => boolean;
}> = ({ strokes, historyLoading, cursors, participants, meId, connected, onStroke, onCursor, onUndo }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tool, setTool] = useState<'pen' | 'eraser'>('pen');
  const [color, setColor] = useState(BRUSH_COLORS[0]);
  const [width, setWidth] = useState(4);
  const drawing = useRef(false);
  const currentPoints = useRef<[number, number][]>([]);
  const lastSentAt = useRef(0);
  const strokeIdRef = useRef<string>('');

  // 座標は常に 0.0〜1.0 で保持し、描画時にキャンバスサイズを掛ける
  const draw = useCallback(() => {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);

    const paint = (s: { tool: string; color: string; width: number; points: [number, number][] }) => {
      if (s.points.length === 0) return;
      ctx.beginPath();
      ctx.strokeStyle = s.tool === 'eraser' ? '#ffffff' : s.color;
      ctx.lineWidth = (s.tool === 'eraser' ? s.width * 3 : s.width) * (c.width / 1000);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.moveTo(s.points[0][0] * c.width, s.points[0][1] * c.height);
      for (const [x, y] of s.points.slice(1)) ctx.lineTo(x * c.width, y * c.height);
      ctx.stroke();
    };

    for (const s of strokes) paint(s);
    // 描きかけの線（送信前でも即座に見える）
    if (currentPoints.current.length > 0) paint({ tool, color, width, points: currentPoints.current });
  }, [strokes, tool, color, width]);

  useEffect(() => { draw(); }, [draw]);

  const pos = (e: React.PointerEvent): [number, number] => {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    return [
      Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    ];
  };

  /** 50msごとに差分を送る（サーバー上限30通/秒に対する配分） */
  const flushIfDue = () => {
    if (Date.now() - lastSentAt.current < 50) return;
    lastSentAt.current = Date.now();
    // 描きかけを都度送ると重複するため、ここでは送らない（確定時に送る方式）
  };

  const start = (e: React.PointerEvent) => {
    if (!connected) return;
    drawing.current = true;
    strokeIdRef.current = `${meId || 'me'}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    currentPoints.current = [pos(e)];
    canvasRef.current?.setPointerCapture(e.pointerId);
    draw();
  };

  const move = (e: React.PointerEvent) => {
    const p = pos(e);
    onCursor(p[0], p[1]); // 実送信は200ms間隔（フック側で間引き）
    if (!drawing.current) return;
    currentPoints.current.push(p);
    flushIfDue();
    draw();
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const points = currentPoints.current;
    currentPoints.current = [];
    if (points.length === 0) return;
    // ローカルに即反映（seqはサーバーから返ったら差し替わる）
    onStroke({ seq: -1, id: strokeIdRef.current, authorId: meId || '', tool, color, width, points });
  };

  return (
    <div>
      {/* 操作は4つ以内: ペン / 消しゴム / 太さ・色 / 取り消し（太さ・色は属性パレット＝共通実装） */}
      <BrushControls
        tool={tool} color={color} width={width}
        onTool={setTool} onColor={setColor} onWidth={setWidth}
        trailing={<button onClick={onUndo} className="btn btn-ghost btn-sm min-h-[44px]">ひとつ戻す</button>}
      />

      <div className="relative">
        <canvas
          ref={canvasRef}
          width={1000}
          height={700}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          className="w-full rounded-pop-sm border-2 border-ink bg-white touch-none"
          style={{ aspectRatio: '1000 / 700', cursor: connected ? 'crosshair' : 'not-allowed' }}
          role="img"
          aria-label="みんなで描く共有キャンバス"
        />

        {/* 他者のカーソル（名前つき） */}
        {Object.values(cursors).filter(c => c.userId !== meId).map(c => {
          const idx = participants.findIndex(p => p.userId === c.userId);
          const col = CURSOR_COLORS[(idx < 0 ? 0 : idx) % CURSOR_COLORS.length];
          const name = participants.find(p => p.userId === c.userId)?.displayName || '誰か';
          return (
            <span key={c.userId} className="absolute pointer-events-none flex items-center gap-1"
              style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%`, transform: 'translate(-2px,-2px)' }} aria-hidden>
              <span className="w-2.5 h-2.5 rounded-full border-2 border-white" style={{ backgroundColor: col }} />
              <span className="text-[10px] font-bold px-1 py-0.5 rounded text-white" style={{ backgroundColor: col }}>{name}</span>
            </span>
          );
        })}

        {/* 履歴を読み込み中はその事実を出す（done:true まで） */}
        {historyLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-pop-sm">
            <span className="text-sm text-ink-2">これまでの線を読み込んでいます…</span>
          </div>
        )}
      </div>
    </div>
  );
};
